/**
 * `schedule` — opening hours for one date, with the museum's own notice.
 *
 * `highlight`/`actions` follow `PriceTable`'s rule exactly (§11.6/§11.4): a
 * resolved argument picks rows out of the FULL table by their own `key`, and
 * the card-level `actions` ride through the shared `CardActions`.
 *
 * One card, two shapes: a single object's rows are «Часы работы» / «Кассы»; a
 * route checked in one `schedule_on_date` call has a row PER OBJECT, the ticket
 * office in its `note`, and `closed` on a stop the museum has shut that day —
 * drawn apart (`table__row--closed`), since that is the row a visitor must not
 * miss. Past eight rows `CollapsibleRows` folds the tail as it does anywhere.
 */

import type { ScheduleCardProps } from '../generated/schemas';
import { cn } from '../../lib/cn';
import { stripEmoji as plain } from '../../lib/emoji';
import CardActions from './CardActions';
import CollapsibleRows from './CollapsibleRows';

type ScheduleRow = ScheduleCardProps['rows'][number];

export default function ScheduleCard({ props }: { props: ScheduleCardProps }) {
  return (
    <article className="card card--schedule">
      <header className="card__head">
        <h3 className="card__title">Расписание</h3>
        {props.date_label ? (
          <span className="card__meta">
            {plain(props.date_label)}
            {props.weekday_label ? `, ${plain(props.weekday_label)}` : ''}
          </span>
        ) : null}
      </header>
      {props.notice ? <p className="notice notice--warn">{plain(props.notice)}</p> : null}
      <div className="table-wrap">
        <table className="table">
          <tbody>
            <CollapsibleRows<ScheduleRow>
              rows={props.rows}
              highlight={props.highlight}
              keyOf={(row) => row.key}
              renderRow={(row, { highlighted, revealed }) => (
                <tr
                  key={row.title}
                  className={cn(
                    highlighted && 'table__row--highlight',
                    row.closed && 'table__row--closed',
                    revealed && 'row-reveal',
                  )}
                >
                  <th scope="row">
                    {plain(row.title)}
                    {row.note ? <span className="table__note">{plain(row.note)}</span> : null}
                  </th>
                  <td>{plain(row.hours)}</td>
                </tr>
              )}
              renderToggle={(button) => (
                <tr className="table__disclosure-row">
                  <td colSpan={2}>{button}</td>
                </tr>
              )}
            />
          </tbody>
        </table>
      </div>
      <CardActions sourceId={props.id} actions={props.actions} />
    </article>
  );
}
